import { useEffect, useRef, useState } from 'react';
import type { AudienceEvent, PublicStream, ReactionEvent, StatusEvent } from '@zip2live/shared';
import type { ChatEvent } from '@zip2live/shared/chat';
import { api } from './api.ts';
import { publishPlaylist } from './playlist-feed.ts';
import { viewerId } from './viewer-id.ts';
import { chatEvent, setChatConnected } from './chat.ts';
import { reactionsArrived } from './reactions.ts';

/**
 * Live stream status. Pushed over a hibernating WebSocket (no polling while waiting);
 * if the socket cannot connect, falls back to a slow poll so the page still updates.
 * The same socket carries live playlists and chat.
 */
export function useStreamStatus(id: string | null, initial?: PublicStream | null) {
  const [stream, setStream] = useState<PublicStream | null>(initial ?? null);
  const [error, setError] = useState<string | null>(null);
  const retry = useRef(0);

  useEffect(() => {
    if (!id) return;
    let closed = false;
    let ws: WebSocket | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let ping: ReturnType<typeof setInterval> | null = null;
    let reconnect: ReturnType<typeof setTimeout> | null = null;

    const refresh = () =>
      api.getStream(id).then(setStream).catch((e: Error) => setError(e.message));

    const connect = () => {
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${proto}://${location.host}/api/v1/streams/${encodeURIComponent(id)}/ws?v=${viewerId()}`);
      ws.onopen = () => {
        retry.current = 0;
        setChatConnected(id, true);
        if (poll) clearInterval(poll);
        poll = null;
        void refresh();
        ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 30_000);
      };
      ws.onmessage = (m) => {
        if (m.data === 'pong') return;
        try {
          const ev = JSON.parse(m.data as string) as StatusEvent | AudienceEvent | ReactionEvent | { type: 'playlist'; seq: number; text: string } | ChatEvent;
          // A private replay's pushed status is the outsider's view; the creator's own page asks
          // the API again, which gives them their video.
          if (ev.type === 'status' && ev.stream.private) void refresh();
          else if (ev.type === 'status') setStream(ev.stream);
          else if (ev.type === 'playlist') publishPlaylist(id, ev.seq, ev.text);
          else if (ev.type === 'audience') {
            setStream((cur) => cur && { ...cur, viewers: ev.viewers, peakViewers: ev.peakViewers, views: ev.views, category: ev.category, likes: ev.likes, reactions: ev.reactions });
          } else if (ev.type === 'reactions') reactionsArrived(id, ev.counts);
          else if (ev.type.startsWith('chat')) chatEvent(id, ev as ChatEvent);
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        if (ping) clearInterval(ping);
        setChatConnected(id, false);
        if (closed) return;
        poll ??= setInterval(refresh, 10_000);
        const delay = Math.min(30_000, 1000 * 2 ** retry.current++);
        reconnect = setTimeout(connect, delay);
      };
    };

    void refresh();
    connect();
    return () => {
      closed = true;
      ws?.close();
      if (poll) clearInterval(poll);
      if (ping) clearInterval(ping);
      if (reconnect) clearTimeout(reconnect);
    };
  }, [id]);

  return { stream, setStream, error };
}
